import { apiClient } from '@/lib/api-client';
import { ROLES } from '@/constants/roles';
import { uniqueUsersById } from '@/utils/userPicker';

// User types
export interface UserRole {
    id: string;
    userId: string;
    roleId: string;
    role: {
        /** Role UUID — NOT the slug. Never compare this against ROLES.*.slug. */
        id: string;
        /** Canonical identifier (e.g. 'role-owner'). Null on legacy rows. */
        slug: string | null;
        name: string;
    };
}

export interface UserPermission {
    id: string;
    userId: string;
    permissionId: string;
    permission: {
        id: string;
        name: string;
    };
}

export interface Organization {
    id: string;
    name: string;
    type: string;
}

export interface User {
    id: string;
    organizationId: string;
    fullName: string;
    email: string;
    passwordHash?: string;
    isOwner: boolean;
    createdAt: string;
    updatedAt: string;
    verified: boolean;
    status: 'ACTIVE' | 'INACTIVE';
    address?: string | null;
    phone?: string | null;
    jobTitle?: string | null;
    managerId?: string | null;
    organization: Organization;
    roles: UserRole[];
    permissions: UserPermission[];
}

export interface AssignableRep {
    id: string;
    fullName: string;
}

export interface AssignableRepsResponse {
    data: AssignableRep[];
}

export interface UsersResponse {
    data: User[];
    pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
        hasNext: boolean;
        hasPrev: boolean;
    };
}

export interface GetUsersParams {
    page?: number;
    limit?: number;
    sortOrder?: 'asc' | 'desc';
    organizationId?: string;
    roleIds?: string;
    permissionIds?: string;
    search?: string;
    status?: 'ACTIVE' | 'INACTIVE' | 'PENDING';
}

// Users API
export const usersApi = {
    // Get users with filtering and pagination
    getUsers: async (params: GetUsersParams = {}): Promise<UsersResponse> => {
        const searchParams = new URLSearchParams();

        if (params.page) searchParams.append('page', params.page.toString());
        if (params.limit) searchParams.append('limit', params.limit.toString());
        if (params.sortOrder) searchParams.append('sortOrder', params.sortOrder);
        if (params.organizationId) searchParams.append('organizationId', params.organizationId);
        if (params.roleIds) searchParams.append('roleIds', params.roleIds);
        if (params.permissionIds) searchParams.append('permissionIds', params.permissionIds);
        if (params.search) searchParams.append('search', params.search);
        if (params.status) searchParams.append('status', params.status);

        const queryString = searchParams.toString();
        const url = queryString ? `/users?${queryString}` : '/users';

        const response = await apiClient.get<UsersResponse>(url);
        return {
            ...response,
            data: uniqueUsersById(response.data),
        };
    },

    // Get assignable sales reps for client creation.
    // Gated by client-create permission on the back end (not perm-user-read),
    // so plain Sales Reps can populate the rep dropdown in create mode.
    getAssignableReps: async (): Promise<AssignableRepsResponse> => {
        return apiClient.get<AssignableRepsResponse>('/users/assignable-reps');
    },

    // Get managers specifically
    getManagers: async (params: Omit<GetUsersParams, 'roleIds'> = {}): Promise<UsersResponse> => {
        return usersApi.getUsers({
            ...params,
            roleIds: ROLES.SALES_MANAGER.slug
        });
    },

    // Get user by ID
    getUserById: async (userId: string): Promise<User> => {
        return apiClient.get<User>(`/users/${userId}`);
    },

    // Update user
    updateUser: async (userId: string, userData: Partial<User>): Promise<User> => {
        return apiClient.patch<User>(`/users/${userId}`, userData);
    },

    // Change password
    changePassword: async (passwordData: {
        currentPassword: string;
        newPassword: string;
        repeatNewPassword: string;
    }): Promise<void> => {
        return apiClient.patch<void>('/users/password', passwordData);
    },
    // Initiate email update
    initiateEmailUpdate: async (payload: { newEmail: string; profileUpdate: boolean; userId: string }): Promise<{ message: string; newEmail: string }> => {
        return apiClient.post<{ message: string; newEmail: string }>('/users/email-update/initiate', payload);
    },
    // Verify email update
    verifyEmailUpdate: async (payload: { verificationCode: string }): Promise<{ message: string; user: User }> => {
        return apiClient.post<{ message: string; user: User }>('/users/email-update/verify', payload);
    },

    // Search users by name
    searchUsers: async (params: {
        fullName: string;
    }): Promise<UsersResponse> => {
        try {
            const queryParams = new URLSearchParams({
                fullName: params.fullName,
            });

            const response = await apiClient.get<UsersResponse>(
                `/users/search?${queryParams.toString()}`
            );
            return {
                ...response,
                data: uniqueUsersById(response.data),
            };
        } catch (error: any) {
            console.error('Error searching users:', error);
            throw error;
        }
    }
};